import React, { useState, useEffect } from 'react';
import NavigationBreadcrumbs from '../../components/ui/NavigationBreadcrumbs';
import ChecklistServiceTree from './components/ChecklistServiceTree';
import ChecklistDataGrid from './components/ChecklistDataGrid';
import ChecklistDetailPanel from './components/ChecklistDetailPanel';
import BulkOperationsPanel from './components/BulkOperationsPanel';
import ChecklistServiceModal from './components/ChecklistServiceModal';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import { checklistService } from '../../services/supabaseService';

const ServiceAndChecklist = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('checklists');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showBulkOperations, setShowBulkOperations] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  
  // États pour le chargement
  const [currentUser, setCurrentUser] = useState({
    name: "Admin ChecklistPro",
    email: "admin@checklistpro.com",
    role: "admin"
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    complianceStatus: 'all',
    frequency: 'all'
  });

  useEffect(() => {
    setShowBulkOperations(selectedItems.length > 0);
  }, [selectedItems]);

  const handleSaveChecklist = async (checklistData) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Saving checklist/service:', checklistData);
      console.log('Mode:', modalMode);
      console.log('Type:', checklistData.type);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (modalMode === 'create') {
        console.log('Creating new item:', checklistData);
        // Add your create logic here
        await checklistService.createChecklist(checklistData);
      } else {
        console.log('Updating item:', checklistData);
        // Add your update logic here
        if (checklistData.type === 'checklist') {
          setSelectedChecklist(checklistData);
        } else {
          setSelectedService(checklistData);
        }
      }
      
      setShowChecklistModal(false);
      
    } catch (err) {
      console.error('Error saving item:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChecklistSelect = (checklist) => {
    setSelectedChecklist(checklist);
    setSelectedService(null);
    setSelectedCategory(null);
    setSelectedItem(checklist);
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setSelectedChecklist(null);
    setSelectedCategory(null);
    setSelectedItem(service);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSelectedChecklist(null);
    setSelectedService(null);
    setSelectedItem(null);
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
  };

  const handleSelectionChange = (items) => {
    setSelectedItems(items);
  };

  const handleCreateItem = () => {
    setModalMode('create');
    setSelectedItem(null);
    setShowChecklistModal(true);
  };

  const handleEditItem = (item) => {
    setModalMode('edit');
    setSelectedItem(item);
    setShowChecklistModal(true);
  };

  const handleBulkAction = (actionId, items, data) => {
    console.log('Bulk action:', actionId, items, data);
    
    try {
      // Simulate different bulk actions
      switch (actionId) {
        case 'duplicate-checklist':
          console.log(`Duplicating ${items.length} checklists`);
          break;
        case 'update-status':
          console.log(`Updating status to "${data?.value}" for ${items.length} items`);
          break;
        case 'assign-technician':
          console.log(`Assigning technician to ${items.length} services`);
          break;
        case 'export-data':
          console.log(`Exporting data for ${items.length} items`);
          break;
        case 'archive-items':
          console.log(`Archiving ${items.length} items`);
          break;
        default:
          console.log(`Unknown action: ${actionId}`);
      }
    } catch (err) {
      console.error('Error in bulk action:', err);
      setError(err.message);
    }
    
    // Clear selection after action
    setSelectedItems([]);
  };

  const handleClearSelection = () => {
    setSelectedItems([]);
  };

  const handleItemUpdate = async (updatedItem) => {
    try {
      console.log('Item updated:', updatedItem);
      // Call your service here
      if (updatedItem.type === 'checklist') {
        await checklistService.updateChecklist(updatedItem.id, updatedItem);
        setSelectedChecklist(updatedItem);
      } else {
        await checklistService.updateService(updatedItem.id, updatedItem);
        setSelectedService(updatedItem);
      }
      setSelectedItem(updatedItem);
    } catch (err) {
      console.error('Error updating item:', err);
      setError(err.message);
    }
  };

  const handleQuickAction = (actionId, items) => {
    try {
      switch (actionId) {
        case 'create-new': 
          handleCreateItem();
          break;
        case 'import-data':
          setShowImportModal(true);
          break;
        case 'export-data':
          console.log('Exporting checklist data');
          break;
        case 'clear-selection':
          handleClearSelection();
          break;
        default:
          handleBulkAction(actionId, items, {});
      }
    } catch (err) {
      console.error('Error in quick action:', err);
      setError(err.message);
    }
  };

  // Mock data pour checklists et services
  const mockChecklists = [
    {
      id: 1,
      type: 'checklist',
      name: "Inspection Sécurité Échafaudage",
      code: "CHK-SEC-001",
      description: "Checklist complet pour l'inspection des échafaudages selon normes EN 12811",
      category: "sécurité",
      complianceStatus: "compliant",
      frequency: "monthly",
      version: "2.1",
      lastUpdated: "2024-11-15",
      status: "active",
      itemsCount: 45,
      requiredItems: 45,
      nextRevisionDate: "2024-12-15",
      services: [
        {
          id: 101,
          type: 'service',
          name: "Service Inspection Hebdomadaire",
          checklistName: "Inspection Sécurité Échafaudage",
          code: "SRV-INS-001",
          description: "Inspection hebdomadaire complète des échafaudages",
          category: "inspection",
          duration: "2 heures",
          price: 150,
          frequency: "weekly",
          complianceStatus: "compliant",
          technicianLevel: "certified",
          equipmentRequired: ["Télémètre laser", "Niveau numérique"]
        }
      ]
    },
    {
      id: 2,
      type: 'checklist',
      name: "Contrôle Équipements de Levage",
      code: "CHK-LEV-001",
      description: "Vérification périodique des équipements de levage et accessoires",
      category: "levage",
      complianceStatus: "pending",
      frequency: "quarterly",
      version: "1.3",
      lastUpdated: "2024-10-20",
      status: "active",
      itemsCount: 32,
      requiredItems: 28,
      nextRevisionDate: "2025-01-20",
      services: [
        {
          id: 102,
          type: 'service',
          name: "Contrôle Trimestriel Ponts Roulants",
          checklistName: "Contrôle Équipements de Levage",
          code: "SRV-LEV-001",
          description: "Contrôle réglementaire trimestriel des ponts roulants",
          category: "maintenance",
          duration: "4 heures",
          price: 320,
          frequency: "quarterly",
          complianceStatus: "pending",
          technicianLevel: "expert",
          equipmentRequired: ["Dynamomètre", "Jauges de contrainte"]
        }
      ]
    },
    {
      id: 3,
      type: 'checklist',
      name: "Audit Sécurité Incendie",
      code: "CHK-INC-001",
      description: "Audit complet des installations de sécurité incendie",
      category: "sécurité",
      complianceStatus: "non-compliant",
      frequency: "annual",
      version: "3.0",
      lastUpdated: "2024-09-10",
      status: "active",
      itemsCount: 58,
      requiredItems: 58,
      nextRevisionDate: "2024-12-10",
      services: [
        {
          id: 103,
          type: 'service',
          name: "Test Extincteurs Mensuel",
          checklistName: "Audit Sécurité Incendie",
          code: "SRV-INC-001",
          description: "Test mensuel de tous les extincteurs sur site",
          category: "test",
          duration: "1.5 heures",
          price: 85,
          frequency: "monthly",
          complianceStatus: "compliant",
          technicianLevel: "basic",
          equipmentRequired: ["Manomètre", "Balance de test"]
        },
        {
          id: 104,
          type: 'service',
          name: "Vérification Annuelle RIA",
          checklistName: "Audit Sécurité Incendie",
          code: "SRV-INC-002",
          description: "Vérification annuelle des robinets d'incendie armés",
          category: "inspection",
          duration: "3 heures",
          price: 210,
          frequency: "annual",
          complianceStatus: "non-compliant",
          technicianLevel: "certified",
          equipmentRequired: ["Manomètre haute pression", "Débitmètre"]
        }
      ]
    }
  ];

  // Categories pour le tree view
  const categories = [
    { id: 'cat-1', name: 'Sécurité', count: 24, color: 'red' },
    { id: 'cat-2', name: 'Qualité', count: 18, color: 'blue' },
    { id: 'cat-3', name: 'Maintenance', count: 32, color: 'green' },
    { id: 'cat-4', name: 'Inspection', count: 15, color: 'purple' },
    { id: 'cat-5', name: 'Audit', count: 8, color: 'orange' }
  ];

  // Données pour la grid selon le mode de vue
  const gridData = viewMode === 'checklists' 
    ? mockChecklists 
    : mockChecklists.flatMap(checklist => 
        checklist.services.map(service => ({
          ...service,
          checklistName: checklist.name,
          checklistCategory: checklist.category
        }))
      );

  const breadcrumbs = [
    { label: 'Dashboard', href: '/' },
    { label: 'Gestion des Checklists', href: '/checklists' },
    { label: viewMode === 'checklists' ? 'Checklists' : 'Services', href: '#' }
  ];

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader
        userRole="admin"
        userName="Admin ChecklistPro"
      />
      
      <main className={`pt-16 transition-layout`}>
        <div className="p-6">
          <NavigationBreadcrumbs items={breadcrumbs} />
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Service et point de controle   </h1>
              <p className="text-muted-foreground mt-1">
                Créez point controle, assignez des points controle au services pour l'intervention.
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Select
                options={[
                  { value: 'checklists', label: 'Vue points controle' },
                  { value: 'services', label: 'Vue Services' }
                ]}
                value={viewMode}
                onChange={setViewMode}
                className="w-48"
              />
              
            
              
              <Button 
                variant="default" 
                iconName="Plus" 
                iconPosition="left" 
                iconSize={16}
                onClick={handleCreateItem}
                disabled={loading}
              >
                Nouveau {viewMode === 'checklists' ? 'point controle' : 'Service'}
              </Button>
            </div>
          </div>

          {/* Error display pour les opérations */}
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg text-red-700">
              {error}
              <button 
                onClick={() => setError(null)} 
                className="ml-2 text-red-800 hover:text-red-900"
              >
                ×
              </button>
            </div>
          )}

          {/* Loading State pour les opérations */}
          {loading && (
            <div className="flex justify-center items-center py-8">
              <div className="text-muted-foreground">Traitement en cours...</div>
            </div>
          )}

          {/* Main Content Layout */}
          {!loading && (
            <div className="grid grid-cols-12 gap-6 h-[calc(100vh-200px)]">
              {/* Left Panel - Checklist/Service Tree (25%) */}
               

              {/* Center Panel - Data Grid (50%) */}
              <div className={`${viewMode === 'checklists' ? 'col-span-12 mx-auto' : 'col-span-12'}`}>
                <ChecklistDataGrid
                  data={gridData}
                  viewMode={viewMode}
                  selectedItems={selectedItems}
                  onSelectionChange={handleSelectionChange}
                  onItemSelect={handleItemSelect}
                  onEditItem={handleEditItem}
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  filters={filters}
                  selectedChecklist={selectedChecklist}
                  selectedService={selectedService}
                />
              </div>
            {/* {viewMode === 'checklists' && (
              <div className="col-span-4">
                <ChecklistDetailPanel
                  checklist={selectedChecklist}
                  onUpdateChecklist={handleItemUpdate}
                />
              </div>
            )}
  */}
              
            </div>
          )}

          {/* Quick Actions Panel */}
          <div className="fixed bottom-6 right-6">
            {/* You can add a QuickActionPanel component here if needed */}
          </div>
        </div>
      </main>

      {/* Bulk Operations Panel */}
      <BulkOperationsPanel
        selectedItems={selectedItems}
        onBulkAction={handleBulkAction}
        onClearSelection={handleClearSelection}
        isVisible={showBulkOperations}
        context={viewMode === 'checklists' ? 'checklists' : 'services'}
      />

      {/* Checklist/Service Modal */}
      <ChecklistServiceModal
        isOpen={showChecklistModal}
        onClose={() => setShowChecklistModal(false)}
        data={selectedItem}
        currentUserRole={currentUser?.role}
        currentUserId={currentUser?.id}
        onSave={handleSaveChecklist}
        mode={modalMode}
        type={viewMode === 'checklists' ? 'checklist' : 'service'}
        loading={loading}
      />
    </div>
  );
};

export default ServiceAndChecklist;